import React, { useState, useEffect, useRef } from 'react'
import { View, TouchableOpacity, Pressable, StyleSheet, ScrollView, Platform } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon, AppModal as Modal, Text } from '@components'
import { Theme } from '@theme'
import { buildRrule, defaultCustomRecurrence, parseRrule, formatRecurrenceLabel } from '@utils'
import { useCreateKeepContact, useUpdateKeepContact, KeepContact } from '@hooks'
import { useScreen } from '@hooks'
import dayjs from 'dayjs'
import DatePicker from 'react-native-date-picker'
import { useLanguage } from '@context'

type FrequencyOption = {
  labelKey: string
  buildRruleFn: (dtstart: Date) => string | null
}

const FREQUENCY_OPTIONS: FrequencyOption[] = [
  { labelKey: 'everyWeek', buildRruleFn: (dtstart) => buildRrule({ type: 'week', dtstart }) },
  { labelKey: 'every2Weeks', buildRruleFn: (dtstart) => buildRrule({ type: 'custom', dtstart, custom: { ...defaultCustomRecurrence, freq: 'week', interval: 2 } }) },
  { labelKey: 'everyMonth', buildRruleFn: (dtstart) => buildRrule({ type: 'month', dtstart }) },
  { labelKey: 'every6Weeks', buildRruleFn: (dtstart) => buildRrule({ type: 'custom', dtstart, custom: { ...defaultCustomRecurrence, freq: 'week', interval: 6 } }) },
  { labelKey: 'every3Months', buildRruleFn: (dtstart) => buildRrule({ type: 'custom', dtstart, custom: { ...defaultCustomRecurrence, freq: 'month', interval: 3 } }) },
  { labelKey: 'every6Months', buildRruleFn: (dtstart) => buildRrule({ type: 'custom', dtstart, custom: { ...defaultCustomRecurrence, freq: 'month', interval: 6 } }) },
  { labelKey: 'everyYear', buildRruleFn: (dtstart) => buildRrule({ type: 'year', dtstart }) },
]

// Three simple dropdowns (Jour / Mois / Année) for picking the event date on
// web — clearer and faster than the native calendar for changing month/year.
const MONTH_NAMES = Array.from({ length: 12 }, (_, i) => dayjs().month(i).format('MMMM'))

const WebDateSelect = ({
  date,
  onChange,
  theme,
}: {
  date: Date
  onChange: (d: Date) => void
  theme: Theme
}) => {
  const d = dayjs(date)
  const year = d.year()
  const years = Array.from({ length: 11 }, (_, i) => year - 2 + i)
  const daysInMonth = d.daysInMonth()
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1)

  const selectStyle: React.CSSProperties = {
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    border: `1px solid ${(theme.colors as any).grey20 ?? '#e0e0e0'}`,
    borderRadius: 10,
    padding: '8px 26px 8px 10px',
    fontSize: 14,
    fontWeight: 600,
    color: (theme.colors as any).black ?? '#1b2a4a',
    backgroundColor: '#ffffff',
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23667085' stroke-width='2'><path d='M6 9l6 6 6-6'/></svg>\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    cursor: 'pointer',
    outline: 'none',
  }

  // Clamp the day when the selected month has fewer days (e.g. 31 -> Feb).
  const setPart = (next: dayjs.Dayjs) => onChange(next.toDate())

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <select
        aria-label="Jour"
        value={d.date()}
        onChange={(e) => setPart(d.date(Number(e.target.value)))}
        style={selectStyle}
      >
        {days.map((day) => (
          <option key={day} value={day}>{day}</option>
        ))}
      </select>
      <select
        aria-label="Mois"
        value={d.month()}
        onChange={(e) => setPart(d.month(Number(e.target.value)))}
        style={{ ...selectStyle, flex: 1 }}
      >
        {MONTH_NAMES.map((name, i) => (
          <option key={name} value={i}>{name}</option>
        ))}
      </select>
      <select
        aria-label="Année"
        value={year}
        onChange={(e) => setPart(d.year(Number(e.target.value)))}
        style={selectStyle}
      >
        {years.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  )
}

/** Maps rrule + dtstart to preset index 0..6 or -1 for custom/unknown */
function getPresetIndexFromRrule(rrule: string | null | undefined, dtstart: Date): number {
  if (!rrule?.trim()) return -1
  const { type, custom } = parseRrule(rrule, dtstart)
  if (type === 'week' && !custom) return 0
  if(type === '2weeks' && !custom) return 1
  if(type === '6weeks' && !custom) return 3
  if(type === '3months' && !custom) return 4
  if(type === '6months' && !custom) return 5
  if (type === 'month' && !custom) return 2
  if (type === 'year' && !custom) return 6
  if (type === 'custom' && custom) {
    if (custom.freq === 'week' && custom.interval === 2) return 1
    if (custom.freq === 'week' && custom.interval === 6) return 3
    if (custom.freq === 'month' && custom.interval === 3) return 4
    if (custom.freq === 'month' && custom.interval === 6) return 5
  }
  return -1
}

interface QuickKeepContactDropdownProps {
  visible: boolean
  onClose: () => void
  contactAccountId: string
  contactName: string
  existingKeepContact?: KeepContact | null
  /** On web: position dropdown as contextual menu under this layout (x, y, width, height) */
  anchorLayout?: { x: number; y: number; width: number; height: number } | null
  onCustomize: () => void
}

export const QuickKeepContactDropdown: React.FC<QuickKeepContactDropdownProps> = ({
  visible,
  onClose,
  contactAccountId,
  contactName,
  existingKeepContact,
  anchorLayout,
  onCustomize,
}) => {
  const theme = useTheme()
  const { isDesktop, isWeb } = useScreen()
  const { t } = useLanguage()
  const styles = getStyle(theme)
  const isContextualMenu = isDesktop && anchorLayout
  const dropdownRef = useRef<View>(null)
  const { mutate: createKeepContact } = useCreateKeepContact()
  const { mutate: updateKeepContact } = useUpdateKeepContact()
  const [selectedDate, setSelectedDate] = useState(dayjs().toDate())
  const [selectedPresetIndex, setSelectedPresetIndex] = useState<number>(-1)
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

  useEffect(() => {
    if (!visible) return
    if (existingKeepContact?.keepDate) {
      const keepDate = dayjs(existingKeepContact.keepDate).toDate()
      setSelectedDate(keepDate)
      setSelectedPresetIndex(getPresetIndexFromRrule(existingKeepContact.rrule, keepDate))
    } else {
      setSelectedDate(dayjs().toDate())
      setSelectedPresetIndex(-1)
    }
  }, [visible, existingKeepContact])

  const handleSelectFrequency = (option: FrequencyOption, optionIndex: number) => {
    const date = selectedDate
    const rrule = option.buildRruleFn(date)
    const keepDate = dayjs(date).hour(9).minute(0).toISOString()

    if (existingKeepContact) {
      updateKeepContact({
        id: existingKeepContact.id,
        date: keepDate,
        rrule: rrule ?? null,
      })
    } else {
      createKeepContact({
        name: t('keepContact.keepContactWith', { contactName }),
        contactAccountId,
        date: keepDate,
        rrule: rrule ?? null,
        hasReminder: true,
      })
    }
    setSelectedPresetIndex(optionIndex)
    onClose()
  }

  if (!visible) return null

  const displayDate = existingKeepContact?.keepDate
    ? dayjs(existingKeepContact.keepDate).toDate()
    : selectedDate
  const displayPresetIndex = existingKeepContact?.rrule != null
    ? getPresetIndexFromRrule(existingKeepContact.rrule, displayDate)
    : selectedPresetIndex
  const currentRecurrenceLabel = displayPresetIndex >= 0
    ? (FREQUENCY_OPTIONS[displayPresetIndex]?.labelKey ? t(`recurrence.${FREQUENCY_OPTIONS[displayPresetIndex].labelKey}`) : null)
    : (existingKeepContact?.rrule ? formatRecurrenceLabel(existingKeepContact.rrule, existingKeepContact.keepDate, t) : null)

  const dropdownContent = (
    <View
      ref={dropdownRef}
      style={[styles.dropdown, isContextualMenu && anchorLayout ? { position: 'absolute' as const, left: anchorLayout.x, top: anchorLayout.y + anchorLayout.height + 4, minWidth: 280 } : null]}
      onStartShouldSetResponder={() => true}
    >
      <Text type="bodyBold" style={styles.title}>{t('keepContact.pageTitle')}</Text>
      <View style={styles.dateSection}>
        <Text type="bodySmall" style={styles.dateLabel}>{t('keepContact.eventDate')}</Text>
        {isWeb ? (
          // Simple Day / Month / Year dropdowns (much easier than the native
          // calendar to jump across months and years).
          <WebDateSelect date={selectedDate} onChange={setSelectedDate} theme={theme} />
        ) : isDesktop ? (
          <DatePicker
            modal={false}
            mode="date"
            date={selectedDate}
            onDateChange={setSelectedDate}
          />
        ) : (
          <>
            <TouchableOpacity onPress={() => setIsDatePickerOpen(true)} style={styles.dateButton}>
              <Text type="body" color={theme.colors.primary}>
                {dayjs(selectedDate).format('DD/MM/YYYY')}
              </Text>
              <Icon name="calendar" size={theme.iconSize.small} color={theme.colors.primary} />
            </TouchableOpacity>
            <DatePicker
              modal={true}
              mode="date"
              open={isDatePickerOpen}
              date={selectedDate}
              onConfirm={(date) => {
                setIsDatePickerOpen(false)
                setSelectedDate(date)
              }}
              onCancel={() => setIsDatePickerOpen(false)}
            />
          </>
        )}
      </View>
      <View style={styles.currentSection}>
        <Text type="bodySmall" style={styles.dateLabel}>{t('recurrenceComponent.recurrence')}</Text>
        <Text type="bodySmall" color={theme.colors.secondary}>
          {currentRecurrenceLabel ?? t('keepContact.none')}
        </Text>
      </View>
      <View style={styles.separator} />
      <ScrollView style={styles.optionsScroll} keyboardShouldPersistTaps="handled">
        {FREQUENCY_OPTIONS.map((option, index) => (
          <TouchableOpacity
            key={index}
            style={[styles.option, displayPresetIndex === index ? styles.optionSelected : null]}
            onPress={() => handleSelectFrequency(option, index)}
          >
            <Text
              type="bodySmall"
              style={[styles.optionText, displayPresetIndex === index ? { color: theme.colors.primary, fontWeight: '600' } : {}]}
            >
              {t(`recurrence.${option.labelKey}`)}
            </Text>
          </TouchableOpacity>
        ))}
        <View style={styles.separator} />
        <TouchableOpacity
          style={[styles.option, displayPresetIndex === -1 && existingKeepContact?.rrule != null ? styles.optionSelected : null]}
          onPress={() => {
            onClose()
            onCustomize()
          }}
        >
          <Text type="bodySmall" color={theme.colors.primary}>{t('recurrence.custom')}</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  )

  return (
    <>
      <Modal transparent visible={visible} animationType="fade" onRequestClose={onClose}>
        <View style={[styles.overlay, isContextualMenu ? styles.overlayContextual : null]}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={(ev) => {
              if (Platform.OS === 'web' && dropdownRef.current != null && ev?.nativeEvent?.target) {
                const el = dropdownRef.current as unknown as HTMLElement
                if (el.contains(ev.nativeEvent.target as unknown as Node)) return
              }
              onClose()
            }}
          />
          {dropdownContent}
        </View>
      </Modal>
    </>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    overlay: {
      pointerEvents: 'box-none',
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.3)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    overlayContextual: {
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
    },
    dropdown: {
      backgroundColor: theme.colors.white,
      borderRadius: 12,
      paddingVertical: 8,
      width: 280,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 2px 4px rgba(0,0,0,0.25)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 4,
            elevation: 5,
          }),
    },
    title: {
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.lightGrey,
    },
    dateSection: {
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.lightGrey,
    },
    dateLabel: {
      marginBottom: 4,
      color: theme.colors.secondary,
      fontWeight: '600',
    },
    dateButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      justifyContent: 'space-between',
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.spacing.sm,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
      alignSelf: 'flex-start',
      backgroundColor: theme.colors.white,
    },
    currentSection: {
      paddingHorizontal: 16,
      paddingVertical: 8,
    },
    optionsScroll: {
      maxHeight: 280,
    },
    option: {
      paddingHorizontal: 16,
      paddingVertical: 12,
    },
    optionSelected: {
      backgroundColor: theme.colors.primary + '15',
    },
    optionText: {
      color: theme.colors.secondary,
    },
    separator: {
      height: 1,
      backgroundColor: theme.colors.lightGrey,
      marginHorizontal: 16,
    },
  })
